import * as React from "react";
import { useState } from "react";

import { Field, withTypes } from "react-final-form";
import axios from "axios";

import {
  Button,
  CardActions,
  CircularProgress,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

import {
  Notification,
  useNotify,
} from "react-admin";

import { ENDPOINTS } from "../apiConstants";
import { getConfigGet, getConfigPost } from "../getConfig";
import { ShowData } from "./transferRewardAmount";

const useStyles = makeStyles((theme) => ({
  main: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "flex-start",
    backgroundRepeat: "no-repeat",
    backgroundSize: "cover",
  },
  form: {
    padding: "0 0.5em 0.5em 0.5em",
  },
  actions: {
    padding: "0 1em 1em 1em",
  },
  inputContainer: {
    display: "flex",
    gap: "10px",
    alignItems: "center",
  },
  tableContainer: {
    margin: "20px 0",
  },
  updateForm: {
    padding: "15px",
    border: "1px solid #e0e0e0",
    borderRadius: "8px",
    backgroundColor: "#f0f8ff",
    marginTop: "15px",
    maxWidth: "700px",
    margin: "15px auto 0 auto",
  },
}));

const renderInput = ({
  meta: { touched, error } = { touched: false, error: undefined },
  input: { ...inputProps },
  style,
  ...props
}: any) => (
  <TextField
    error={!!(touched && error)}
    helperText={touched && error}
    {...inputProps}
    {...props}
    style={style}
  />
);

interface FormValues {
  email?: string;
  field?: string;
  value?: string;
  user_beneficiary_id?: string;
}

const { Form } = withTypes<FormValues>();

const ALLOWED_FIELDS = [
  { value: 'beneficiary_reference', label: 'Beneficiary Reference' },
  { value: 'remitter_reference', label: 'Remitter Reference' },
  { value: 'sort_code', label: 'Sort Code' },
  { value: 'account_number', label: 'Account Number' },
];

const UpdatePaymentReference = () => {
  const classes = useStyles();
  const notify = useNotify();

  const [loading, setLoading] = useState(false);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [paymentData, setPaymentData] = useState<any[]>([]);
  const [selectedField, setSelectedField] = useState<string>('');
  const [searchEmail, setSearchEmail] = useState<string>('');

  const handleSearch = (inputData: any) => {
    setLoading(true);
    setPaymentData([]);
    setSelectedField('');
    notify("Searching for payment references...");

    const { email } = inputData;

    if (!email) {
      notify("Please enter an email address", "error", "", undefined, 5000);
      setLoading(false);
      return;
    }

    setSearchEmail(email);

    let config = getConfigGet({ email });

    axios
      .get(ENDPOINTS.UPDATE_PAYMENT_REFERENCE, config)
      .then((res: any) => {
        setLoading(false);
        const data = res?.data;
        if (data && Array.isArray(data) && data.length > 1) {
          setPaymentData(data);
          notify(`Found ${data.length - 1} payment reference record(s)`, "info", "", undefined, 5000);
        } else {
          notify("No payment reference records found", "error", "", undefined, 5000);
        }
      })
      .catch((error) => {
        setLoading(false);
        notify(
          error?.response?.data?.message || "Failed to search for payment references",
          "error",
          "",
          undefined,
          5000
        );
      });
  };

  const handleUpdate = (inputData: any) => {
    if (!paymentData || paymentData.length === 0) {
      notify("Please search for a user first", "error", "", undefined, 5000);
      return;
    }

    const { field, value } = inputData;
    const user_beneficiary_id = paymentData[1]?.[0];

    if (!field) {
      notify("Please select a field", "error", "", undefined, 5000);
      return;
    }

    if (!value) {
      notify("Please enter a value", "error", "", undefined, 5000);
      return;
    }

    if (!user_beneficiary_id) {
      notify("No record found to update", "error", "", undefined, 5000);
      return;
    }

    setUpdateLoading(true);
    notify("Updating payment reference...");

    const data = { field, value, user_beneficiary_id };

    const config = {
      headers: getConfigPost(),
      params: { email: searchEmail }
    };

    axios
      .patch(ENDPOINTS.UPDATE_PAYMENT_REFERENCE, data, config)
      .then((res: any) => {
        setUpdateLoading(false);
        if (res?.data?.success) {
          const label = ALLOWED_FIELDS.find(f => f.value === field)?.label || field;
          notify(`${label} updated successfully`, "info", "", undefined, 5000);
          setSelectedField('');
          handleSearch({ email: searchEmail });
        } else {
          notify("Failed to update payment reference", "error", "", undefined, 5000);
        }
      })
        .catch((error) => {
        setUpdateLoading(false);
        notify(
          error?.response?.data?.message || "Failed to update payment reference",
          "error",
          "",
          undefined,
          5000
        );
      });
  };

  const validate = (values: FormValues) => {
    const errors: FormValues = {};
    if (!values.email) {
      errors.email = "Email is required";
    }
    return errors;
  };

  return (
    <>
      <Form
        onSubmit={handleSearch}
        validate={validate}
        render={({ handleSubmit, form }) => (
          <div>
            <form onSubmit={handleSubmit} noValidate>
              <div className={classes.main}>
                <div style={{
                  padding: "20px",
                  border: "1px solid #e0e0e0",
                  borderRadius: "8px",
                  backgroundColor: "#f9f9f9",
                  marginBottom: "20px",
                  maxWidth: "600px",
                  margin: "0 auto 20px auto"
                }}>
                  <h3 style={{ marginTop: 0, marginBottom: "20px", color: "#333" }}>Search Payment References</h3>
                  <div className={classes.form}>
                    <div className={classes.inputContainer}>
                      <Field
                        name="email"
                        component={renderInput}
                        label="Email Address"
                        disabled={loading}
                        style={{ flex: 1 }}
                      />
                    </div>
                  </div>
                  <CardActions className={classes.actions}>
                    <Button
                      variant="contained"
                      type="submit"
                      color="primary"
                      disabled={loading}
                      style={{ marginRight: '10px', flex: 1 }}
                    >
                      {loading && <CircularProgress size={25} thickness={2} />}
                      Search
                    </Button>
                    <Button
                      variant="outlined"
                      color="secondary"
                      onClick={() => {
                        setPaymentData([]);
                        setSelectedField('');
                        setSearchEmail('');
                        form.reset();
                      }}
                      style={{ flex: 1 }}
                    >
                      Clear
                    </Button>
                  </CardActions>
                </div>
                <Notification />
              </div>
            </form>

            {paymentData && paymentData.length > 0 && (
              <div className={classes.tableContainer} style={{ maxWidth: "900px", margin: "20px auto 0 auto" }}>
                <h3 style={{ marginTop: 0, marginBottom: "10px", color: "#333", textAlign: "center" }}>Payment Reference Records</h3>
                <ShowData record={paymentData} tableCenterAlign={true} />
              </div>
            )}

            {paymentData && paymentData.length > 1 && (
              <div style={{
                maxWidth: "700px",
                margin: "15px auto 0 auto",
                padding: "12px",
                backgroundColor: "#fff3cd",
                border: "1px solid #ffeaa7",
                borderRadius: "4px",
                color: "#856404"
              }}>
                <strong>Note:</strong> Once the data is updated, please do the Payee update on PPS. Also, check data on the user tab.
              </div>
            )}

            {paymentData && paymentData.length > 1 && (
              <Form
                onSubmit={handleUpdate}
                render={({ handleSubmit, form: updateForm }) => (
                  <form onSubmit={handleSubmit}>
                    <div className={classes.updateForm}>
                      <h3 style={{ marginTop: 0, marginBottom: "15px", color: "#333" }}>Update Payment Reference</h3>
                      <div className={classes.form} style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                        <Field
                          name="field"
                          component={({ input }) => (
                            <FormControl style={{ minWidth: '180px' }}>
                              <InputLabel>Field</InputLabel>
                              <Select
                                {...input}
                                onChange={(e) => {
                                  const val = e.target.value as string;
                                  input.onChange(val);
                                  setSelectedField(val);
                                  updateForm.change('value', '');
                                }}
                                disabled={updateLoading}
                              >
                                {ALLOWED_FIELDS.map((option) => (
                                  <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          )}
                        />
                        <Field
                          name="value"
                          component={renderInput}
                          label={`New ${selectedField ? ALLOWED_FIELDS.find(f => f.value === selectedField)?.label : 'Value'}`}
                          disabled={updateLoading || !selectedField}
                          style={{ minWidth: '150px' }}
                        />
                        <Button
                          variant="contained"
                          color="primary"
                          disabled={updateLoading || !selectedField || !updateForm.getState().values.value}
                          style={{ width: '80px' }}
                          onClick={() => {
                            const formValues = updateForm.getState().values;
                            handleUpdate(formValues);
                          }}
                        >
                          {updateLoading && <CircularProgress size={25} thickness={2} />}
                          Update
                        </Button>
                      </div>
                    </div>
                  </form>
                )}
              />
            )}
          </div>
        )}
      />
    </>
  );
};

export default UpdatePaymentReference;
